import React, { useState, useEffect, useMemo } from "react";
import { Table, Form, Modal, Button } from "react-bootstrap";
import ExportToExcel from "../components/ExportToExcel";
import Select from "react-select";
import Pagination from "../components/Pagination";
import SearchBar from "../components/SearchBar";
import { useNavigate } from "react-router-dom";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import moment from "moment";
import { useSelector } from "react-redux";
import { FaSpinner } from "react-icons/fa";

const MultiAction = ({
  data = [], // Set default value to an empty array if data is undefined or null
  columns,
  defaultColumns,
  // itemsPerPage,
  searchTerm,
  setSearchTerm,
  isRequiredStatus,
  isApiStatus,
  handleIcon1,
  handleIcon2,
  handleIcon3,
  handleIcon4,
  handleIcon5,
  showIcon1,
  showIcon2,
  showIcon3,
  showIcon4,
  showIcon5,
  icons,
  addNewPath,
  handleShowModalInParent,
  modalLoading,
  modalData,
  // Optional: (row) => className, so a page can highlight rows of its own
  rowClassName,
}) => {
  const [visibleColumns, setVisibleColumns] = useState(defaultColumns);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [selectedRecordsPerPage, setSelectedRecordsPerPage] = useState(10);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [modalHrFilter, setModalHrFilter] = useState(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const toggleColumn = (columnName) => {
    setVisibleColumns({
      ...visibleColumns,
      [columnName]: !visibleColumns[columnName],
    });
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const navigate = useNavigate();

  const handleEdit = (url, id) => {
    navigate(`${url}${id}`);
  };

  const handleSort = (columnName) => {
    let direction = "ascending";
    if (
      sortConfig &&
      sortConfig.key === columnName &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key: columnName, direction });
  };

  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return []; // Ensure data is an array
    let sortableItems = [...data];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const valA = a[sortConfig.key] ? String(a[sortConfig.key]) : "";
        const valB = b[sortConfig.key] ? String(b[sortConfig.key]) : "";

        // Split the values into alphabetical and numeric parts
        const partsA = valA.match(/(\D+|\d+)/g) || [];
        const partsB = valB.match(/(\D+|\d+)/g) || [];

        for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
          const partA = partsA[i] || "";
          const partB = partsB[i] || "";

          // Determine if parts are numeric or alphabetical
          const isNumericA = !isNaN(partA);
          const isNumericB = !isNaN(partB);

          if (isNumericA && isNumericB) {
            // Both parts are numeric, so compare as numbers
            const numA = parseInt(partA, 10);
            const numB = parseInt(partB, 10);
            if (numA !== numB) {
              return sortConfig.direction === "ascending"
                ? numA - numB
                : numB - numA;
            }
          } else if (isNumericA || isNumericB) {
            // One part is numeric and the other is alphabetical
            return isNumericA ? -1 : 1;
          } else {
            // Both parts are alphabetical, so compare as strings
            if (partA !== partB) {
              return sortConfig.direction === "ascending"
                ? partA.localeCompare(partB)
                : partB.localeCompare(partA);
            }
          }
        }

        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  const filteredData = sortedData.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredData.length);
  const currentData = filteredData.slice(startIndex, endIndex);

  const handleAddNew = () => {
    navigate(addNewPath);
  };

  const ExcelName = useSelector(
    ({ layoutReducer }) => layoutReducer?.filterSlice?.navbarHeading
  );

  const recordsPerPageOption = [
    { value: 10, label: "10" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
    { value: filteredData.length, label: "All" },
  ];

  const handleRecordsPerPage = (e) => {
    const newRecordsPerPage = Number(e.target.value);

    // Calculate the new starting record index based on the current page
    const currentStartIndex = (currentPage - 1) * selectedRecordsPerPage;
    const newPage = Math.floor(currentStartIndex / newRecordsPerPage) + 1;

    // Update state
    setSelectedRecordsPerPage(newRecordsPerPage);
    setCurrentPage(newPage); // Adjust to the new page based on the same starting record index
    setItemsPerPage(newRecordsPerPage);
  };
  return (
    <div>
      <div className="d-flex justify-content-between mb-3">
        <div className="me-1">
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </div>

        <div className="d-flex justify-content-end">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-top">Export to Excel</Tooltip>}
          >
            <div className="me-1">
              <ExportToExcel
                data={filteredData}
                filename={`${ExcelName}.csv`}
              />
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-top">Column Chooser</Tooltip>}
          >
            <div className="btn-group me-2">
              <button
                type="button"
                className="dropdown-toggle skill-button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                id="excel-btn"
              >
                <i className="bi bi-list"></i>
              </button>
              <ul className="dropdown-menu p-2">
                <div className="row">
                  <Form.Group>
                    {Object.keys(columns).map((columnName) => (
                      <Form.Check
                        key={columnName}
                        type="checkbox"
                        id={`checkbox-${columnName}`}
                        label={columns[columnName].text} // Display the column name using 'text' key
                        checked={visibleColumns[columnName] || false} // Use 'visible' key to control the checked state
                        onChange={() => toggleColumn(columnName)} // Toggle visibility on change
                      />
                    ))}
                  </Form.Group>
                </div>
              </ul>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip id="tooltip-top">Add New</Tooltip>}
          >
            <button id="Add-btn" onClick={handleAddNew}>
              <i className="fa fa-plus"></i> Add
            </button>
          </OverlayTrigger>
        </div>
      </div>
      <div className="box-body table-responsive">
     <Table striped bordered hover>
  <thead id="thcolor" className="text-center">
    <tr>
      {Object.keys(columns).map(
        (columnName) =>
          visibleColumns[columnName] && (
            <th key={columnName} onClick={() => handleSort(columnName)}>
              {columns[columnName].text}
              <span className="ms-2">
                {sortConfig.key === columnName ? (
                  sortConfig.direction === "ascending" ? (
                    <i className="bi bi-chevron-up"></i>
                  ) : (
                    <i className="bi bi-chevron-down"></i>
                  )
                ) : (
                  <i className="bi bi-chevron-down"></i>
                )}
              </span>
            </th>
          )
      )}
      <th>Action</th>
    </tr>
  </thead>
  
  {isRequiredStatus ? (
    isApiStatus === 200 && currentData?.length != 0 ? (
      <tbody className="border-bottom">
        {currentData.map((item, index) => (
          <tr key={index} className={rowClassName ? rowClassName(item) : undefined}>
            {Object.keys(columns).map((columnName) => {
              let cellValue = item[columnName];
              const isNumeric = typeof cellValue === "number";
              const isYesOrNo = cellValue === "Yes" || cellValue === "No";
              const isDateColumn = columns[columnName].text
                .toLowerCase()
                .includes("date");
              const isActiveCol = columns[columnName].text === "Active";

              if (isActiveCol) {
                cellValue = cellValue === "Yes" ? "Yes" : "No";
              }
              
              if (columnName === "LeadCount" || columnName === "RmgLeadsCount") {
                return (
                  visibleColumns[columnName] && (
                    <td
                      key={columnName}
                      className="text-center"
                      onClick={() => handleShowModalInParent && handleShowModalInParent(item, item.ID)}
                      style={{ cursor: "pointer", color: "blue" }}
                    >
                      {cellValue}
                    </td>
                  )
                );
              }

              if (columnName === "Title") {
                return (
                  visibleColumns[columnName] && (
                    <td
                      key={columnName}
                      className="text-start"
                    >
                      {cellValue}
                    </td>
                  )
                );
              }

              if (columnName === "RecallRemarks") {
                return (
                  visibleColumns[columnName] && (
                    <td
                      key={columnName}
                      className="text-start"
                      style={{ width: "150px", maxWidth: "150px" }}
                    >
                      {cellValue ? (
                        <OverlayTrigger
                          placement="auto"
                          overlay={
                            <Tooltip id={`tooltip-remarks-${item.ID}`}>
                              {cellValue}
                            </Tooltip>
                          }
                        >
                          <span
                            style={{
                              display: "block",
                              maxWidth: "135px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              cursor: "pointer"
                            }}
                          >
                            {cellValue}
                          </span>
                        </OverlayTrigger>
                      ) : (
                        "-"
                      )}
                    </td>
                  )
                );
              }
              
              return (
                visibleColumns[columnName] && (
                  <td
                    className={
                      isNumeric
                        ? "text-end"
                        : isYesOrNo
                        ? "text-center"
                        : isDateColumn
                        ? "text-center"
                        : "text-start"
                    }
                    key={columnName}
                  >
                    {cellValue}
                  </td>
                )
              );
            })}
            <td className="text-center" style={{ width: "120px" }}>
              <div className="d-flex justify-content-center">
                {showIcon1 && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-top">Edit</Tooltip>}
                  >
                    <div
                      className="me-2"
                      onClick={() => handleIcon1("/postjob/edit/", item.ID)}
                    >
                      {icons.Icon1}
                    </div>
                  </OverlayTrigger>
                )}
                {showIcon2 && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-top">New Application</Tooltip>
                    }
                  >
                    <div
                      disabled={item.IsInActive === "Yes" ? true : false}
                      className={
                        item.IsInActive === "Yes" && item.isNewApplication
                          ? "me-2"
                          : "me-2 icon-disabled"
                      }
                      onClick={() =>
                        item.IsInActive === "Yes" && item.isNewApplication
                          ? handleEdit(
                              "/postjob/new-application/" + item.ID + "/",
                              item.JobMasterID
                            )
                          : {}
                      }
                    >
                      {icons.Icon2}
                    </div>
                  </OverlayTrigger>
                )}
                {showIcon3 && (
                  <div className="me-2" onClick={() => handleIcon3(item)}>
                    {icons.Icon3}
                  </div>
                )}
                {showIcon4 && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-top">Assign TA</Tooltip>}
                  >
                    <div className="me-2" onClick={() => handleIcon4(item)}>
                      {icons.Icon4}
                    </div>
                  </OverlayTrigger>
                )}
                {showIcon5 && (
                  <div className="me-2" onClick={() => handleIcon5(item)}>
                    {icons.Icon5}
                  </div>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    ) : (
      <tbody>
        <tr>
          <td colSpan={Object.keys(columns).length + 1} className="text-center" style={{ padding: "20px" }}>
            {isApiStatus === undefined ? (
              <div>
                <FaSpinner className="spinner me-2" />
                <span>Loading...</span>
              </div>
            ) : (
              <p className="mb-0">No Record Found</p>
            )}
          </td>
        </tr>
      </tbody>
    )
  ) : isApiStatus === 200 && currentData?.length === 0 ? (
      <tbody>
        <tr>
          <td
            colSpan={
              Object.keys(columns).length 
            }
            className="text-center"
          >
            <p>No Data Found</p>
          </td>
        </tr>
      </tbody>
    ) : null}
</Table>
        <div className="d-flex justify-content-between mb-3">
          <div>
            <span className="d-flex">
              Showing {(currentPage - 1) * selectedRecordsPerPage + 1} -{" "}
              {Math.min(
                currentPage * selectedRecordsPerPage,
                filteredData.length
              )}{" "}
              of {filteredData.length} <span className="ms-1">results</span>
              <select
                value={selectedRecordsPerPage}
                onChange={handleRecordsPerPage}
                className="mx-2 form-select-sm"
              >
                {recordsPerPageOption.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </span>
          </div>
          <div>
            <Pagination
              itemsPerPage={itemsPerPage}
              totalItems={filteredData.length}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiAction;
